import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { PosTypeEntity } from "src/app/features/PosType/types/PosTypeEntity";
import type { PosTypeList } from "src/app/features/PosType/types/PosTypeList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const PosTypesTable: FC<ResourceTableProps<PosTypeEntity>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<PosTypeList>): ExtendedColumnsType<PosTypeList>[] => {
            return [
                {
                    title: t("posType.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("posType.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("posType.posMapping"),
                    key: "posMappingName",
                    dataIndex: "posMappingName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "posMappingName" ? sortInfo.order : null,
                },
                {
                    title: t("posType.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("posType.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    render: (value) => displayDateTimeValue(value),
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: PosTypeEntity) => navigate(`${BASE_PATH}/${ResourceName.POS_TYPES}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<PosTypeList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="posTypes_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: PosTypeEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
